import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ShieldCheck } from "lucide-react";

export const PhoneVerifyCard = () => {
  const { user, refresh } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [changing, setChanging] = useState(false);
  const [newPhone, setNewPhone] = useState("");

  const needsPhone = !user?.phone && user?.role === "professional" && user?.approved;
  if (!needsPhone && (!user?.phone || user?.phone_verified !== false)) return null;

  const startCooldown = () => {
    setCooldown(60);
    const t = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; }), 1000);
  };

  const sendCode = async () => {
    try {
      await api.post("/phone/send-code");
      toast.success("6-digit code sent — check your email inbox");
      startCooldown();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const verify = async () => {
    if (code.trim().length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await api.post("/phone/verify", { code: code.trim() });
      toast.success("Phone number verified — thank you! ✅");
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const changeNumber = async () => {
    if (newPhone.trim().length < 7) return toast.error("Enter a valid phone number");
    setBusy(true);
    try {
      await api.post("/phone/change", { phone: newPhone.trim() });
      toast.success("Number updated — a fresh code is on its way");
      setChanging(false); setNewPhone(""); setCode("");
      startCooldown();
      refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (needsPhone) {
    return (
      <div className="card-metal p-6 border-2 border-blue-300 mt-6" data-testid="phone-add-card">
        <h2 className="font-heading font-black text-lg text-slate-800 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-blue-600" /> Never miss work — add your phone
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          We text pros the moment a <b>new booking or cancellation</b> comes in — including the client's GPS location for easy navigation. Add your number and verify it with a 6-digit code.
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Your phone number"
            className="bg-white w-48" data-testid="phone-add-input" />
          <button onClick={changeNumber} disabled={busy} className="btn-red-3d !py-2 !px-5 text-sm" data-testid="phone-add-save">
            {busy ? "Saving…" : "Save & send code"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-metal p-6 border-2 border-amber-400 mt-6" data-testid="phone-verify-card">
      <h2 className="font-heading font-black text-lg text-slate-800 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-amber-600" /> Verify your phone number
      </h2>
      <p className="text-sm text-slate-600 mt-1">
        We sent a <b>6-digit code</b> to <b>{user.phone}</b> by text message (check your email inbox if it didn't arrive).
        {user.role === "client" && <> Booking unlocks once it's verified.</>}
      </p>
      {changing ? (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="New phone number"
            className="bg-white w-48" data-testid="phone-change-input" />
          <button onClick={changeNumber} disabled={busy} className="btn-metal text-sm px-4 py-2" data-testid="phone-change-save">Save & send code</button>
          <button onClick={() => setChanging(false)} className="text-sm text-slate-500 px-2">Cancel</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="6-digit code" inputMode="numeric"
            className="bg-white w-36 text-center font-black tracking-[0.3em]" data-testid="phone-code-input" />
          <button onClick={verify} disabled={busy} className="btn-red-3d !py-2 !px-5 text-sm" data-testid="phone-verify-btn">
            {busy ? "Checking…" : "Verify"}
          </button>
          <button onClick={sendCode} disabled={cooldown > 0}
            className="text-sm font-semibold text-blue-700 hover:underline disabled:text-slate-400 disabled:no-underline"
            data-testid="phone-resend-btn">
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
          </button>
          <button onClick={() => setChanging(true)} className="text-sm text-slate-500 hover:underline" data-testid="phone-change-toggle">
            Wrong number?
          </button>
        </div>
      )}
    </div>
  );
};
